import Phaser from 'phaser';
import { PLAYER_CONFIG } from '../config/game';

export class Player extends Phaser.Physics.Arcade.Sprite {
  private health: number;
  private invincible: boolean = false;
  private parrying: boolean = false;
  private canDash: boolean = true;
  private dashCooldownTimer: number = 0;
  private externalGround: boolean = false; // Set by edge physics

  constructor(scene: Phaser.Scene, x: number, y: number, useSideView: boolean = true) {
    // For boss/platformer scenes, use side-view sprite
    // For top-down scenes, use 3/4 view sprite
    let texture = 'player'; // Default placeholder
    
    if (useSideView && scene.textures.exists('david_side_sprite')) {
      texture = 'david_side_sprite';
    } else if (scene.textures.exists('david_sprite')) {
      texture = 'david_sprite';
    }
    
    super(scene, x, y, texture);

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.health = PLAYER_CONFIG.maxHealth;

    // Physics setup
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(true);
    body.setSize(24, 40);
    body.setOffset(4, 8);
    body.setGravityY(800);  // Strong gravity for snappy jumps
    
    // Scale the sprite appropriately for side-scrolling gameplay
    if (texture === 'david_side_sprite' || texture === 'david_sprite') {
      this.setScale(1.5); // Larger for better visibility in boss scene
    }
  }

  jump() {
    this.jumpWithBoost(1.0);
  }

  jumpWithBoost(boost: number = 1.0) {
    const body = this.body as Phaser.Physics.Arcade.Body;
    // Can jump if on physical ground OR on edge (external ground)
    if (body.blocked.down || body.touching.down || this.externalGround) {
      // Apply boost to jump force (boost > 1 = higher jump)
      const boostedForce = PLAYER_CONFIG.jumpForce * boost;
      body.setVelocityY(boostedForce);
      this.parrying = true;
      this.externalGround = false; // Clear external ground on jump

      // Visual feedback for boosted jump
      if (boost > 1.1) {
        this.setTint(0x88ff88); // Green tint for boosted jump
        this.scene.time.delayedCall(150, () => this.clearTint());
      }

      // Parry window
      this.scene.time.delayedCall(PLAYER_CONFIG.parryWindow, () => {
        this.parrying = false;
      });
    }
  }

  dash(direction: number) {
    if (!this.canDash) return;

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityX(direction * PLAYER_CONFIG.dashForce);

    this.canDash = false;
    this.dashCooldownTimer = PLAYER_CONFIG.dashCooldown;

    // Brief invincibility during dash
    this.invincible = true;
    this.setTint(0x88ccff);

    this.scene.time.delayedCall(200, () => {
      this.invincible = false;
      this.clearTint();
    });
  }

  takeDamage(): boolean {
    if (this.invincible) return true; // Still alive, just invincible

    this.health--;
    this.invincible = true;

    // Flash effect
    this.scene.tweens.add({
      targets: this,
      alpha: 0.3,
      duration: 80,
      yoyo: true,
      repeat: 3,
      onComplete: () => {
        this.setAlpha(1);
      },
    });

    // Knockback
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityY(-150);

    this.scene.time.delayedCall(PLAYER_CONFIG.invincibilityDuration, () => {
      this.invincible = false;
    });

    return this.health > 0; // Return true if still alive
  }

  isOnGround(): boolean {
    const body = this.body as Phaser.Physics.Arcade.Body;
    return body.blocked.down || body.touching.down || this.externalGround;
  }

  // Called by edge physics to set external ground state
  setExternalGround(onGround: boolean) {
    this.externalGround = onGround;
  }

  isParrying(): boolean {
    return this.parrying;
  }

  isInvincible(): boolean {
    return this.invincible;
  }

  isDashing(): boolean {
    return this.invincible && !this.canDash; // Dashing = invincible during dash cooldown start
  }

  getHealth(): number {
    return this.health;
  }

  override setVelocityX(velocity: number): this {
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityX(velocity);

    // Flip sprite based on direction
    if (velocity < 0) {
      this.setFlipX(true);
    } else if (velocity > 0) {
      this.setFlipX(false);
    }
    return this;
  }

  move(direction: number) {
    this.setVelocityX(direction * PLAYER_CONFIG.speed);
  }

  update(_time: number, delta: number) {
    // Update dash cooldown
    if (!this.canDash) {
      this.dashCooldownTimer -= delta;
      if (this.dashCooldownTimer <= 0) {
        this.canDash = true;
      }
    }
  }
}

